import { z } from 'zod'
import { getAuth } from 'firebase/auth'
import { kAgentV2RouterApiBasePath } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { agentV2Request } from '@/agent-v2/conversations/fetch/agent-v2-request'
import { paxtonHighlightAreaSchema } from '@/components/react-pdf-viewer/schema/paxton-highlight-area'
import { getFileNameFromContentDisposition } from '@/util/api/content-disposition'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { ApiError } from '@/util/errors/api-error'

export const AgencyDocumentSchema = z.object({
  title: z.string(),
  source_url: z
    .string()
    .url()
    .refine((value) => ['http:', 'https:'].includes(new URL(value).protocol)),
  media_type: z.string().nullable(),
  publisher_media_type: z.string().nullable().optional(),
  original_available: z.boolean(),
  selected_spans: z.array(z.object({ start: z.number().int().nonnegative(), end: z.number().int().positive(), text: z.string() })),
})
export type AgencyDocument = z.infer<typeof AgencyDocumentSchema>

const AgencyOriginalEvidenceSchema = z.object({ pdf_highlights: z.array(paxtonHighlightAreaSchema) })
export type AgencyPdfHighlights = z.infer<typeof AgencyOriginalEvidenceSchema>['pdf_highlights']

function referenceUrl(conversationId: string | undefined, referenceId: string, endpoint: string, tableId?: string) {
  if (tableId) return `${kPaxtonAppApiRESTBaseUrl()}/api/v1/tables/${encodeURIComponent(tableId)}/references/${encodeURIComponent(referenceId)}/${endpoint}`
  if (!conversationId) throw new Error('Reference owner is required')
  return `${kPaxtonAppApiRESTBaseUrl()}${kAgentV2RouterApiBasePath}/conversations/${encodeURIComponent(conversationId)}/references/${encodeURIComponent(
    referenceId
  )}/${endpoint}`
}

export async function fetchAgencyDocument(
  conversationId: string | undefined,
  referenceId: string,
  signal?: AbortSignal,
  tableId?: string
): Promise<AgencyDocument> {
  return agentV2Request({
    url: referenceUrl(conversationId, referenceId, 'agency-document', tableId),
    method: 'GET',
    signal,
    sentryEndpoint: 'GET /api/v2/agent/conversations/{conversation_id}/references/{reference_id}/agency-document',
    parseResponse: (data, { response }) => {
      const parsed = AgencyDocumentSchema.safeParse(data)
      if (!parsed.success) throw new ApiResponsePayloadError(response.status, 'Invalid agency document response', parsed.error.issues)
      return parsed.data
    },
  })
}

export async function fetchAgencyOriginal(
  conversationId: string | undefined,
  referenceId: string,
  signal?: AbortSignal,
  includeEvidence = false,
  tableId?: string
): Promise<{ blob: Blob; fileName: string | null; pdfHighlights: AgencyPdfHighlights }> {
  const token = await getAuth().currentUser?.getIdToken()
  if (!token) throw new Error('User not authenticated')
  const url = referenceUrl(conversationId, referenceId, 'agency-original', tableId) + (includeEvidence ? '?include_evidence=true' : '')
  const response = await fetch(url, {
    signal,
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!response.ok) throw new ApiError(response.status, 'Original decision is unavailable')
  const fileName = getFileNameFromContentDisposition(response.headers.get('Content-Disposition'))
  if (!includeEvidence) return { blob: await response.blob(), fileName, pdfHighlights: [] }
  try {
    const parts = await response.formData()
    const blob = parts.get('original')
    const evidence = parts.get('evidence')
    if (!blob || typeof blob === 'string' || typeof evidence !== 'string') {
      throw new Error('Missing original or evidence part')
    }
    const parsed = AgencyOriginalEvidenceSchema.safeParse(JSON.parse(evidence))
    if (!parsed.success) throw new Error('Invalid evidence metadata')
    return { blob, fileName, pdfHighlights: parsed.data.pdf_highlights }
  } catch {
    throw new ApiResponsePayloadError(response.status, 'Invalid agency original viewing response')
  }
}
